import type { FC } from "react";

import type { ActionAction, Pages } from "~/core";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";
import { VipStarIcon } from "~/icons";
import {
  Accordion,
  Background,
  Flex,
  Image,
  Typography,
  useUiKitComponents,
} from "~/shared/ui-kit";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

import type { MenuItemType } from "../HeaderV3";

type NavbarItemMobileProps = MenuItemType & {
  closeBurgerMenu: () => void;
  clickHeaderSub: (targetUrl: string) => void;
  clickHeaderMain: (targetUrl: string) => void;
  getUrl: (page: Pages) => string;
  onCallAction: (action: ActionAction) => void;
};

export const NavbarItemMobile: FC<NavbarItemMobileProps> = ({
  id,
  variant,
  translations,
  items,
  closeBurgerMenu,
  clickHeaderSub,
  clickHeaderMain,
  action,
  getUrl,
  onCallAction,
}) => {
  const { Link } = useUiKitComponents();
  const { getDirectusImage } = useDirectus();

  const nestedAction = action?.action?.[0];
  let href: string | undefined;
  if (isExternalLink(nestedAction?.item)) {
    href = nestedAction.item.href ?? undefined;
  }
  if (isInternalLink(nestedAction?.item)) {
    href = nestedAction.item.page ? getUrl(nestedAction.item.page) : undefined;
  }

  const translation = translations?.[0];

  return (
    <div className="lg:hidden first:mt-10">
      {variant === "default" && (
        <Accordion.Item
          variant="secondary"
          key={id}
          title={
            <Link href={href}>
              <Typography size="large" weight="semibold">
                {translation?.name}
              </Typography>
            </Link>
          }
        >
          {items?.map((item) => {
            const itemAction =
              item?.menu_action_sub_items_id?.action?.action?.[0];
            let itemHref: string | undefined;
            if (isExternalLink(itemAction?.item)) {
              itemHref = itemAction.item.href ?? undefined;
            }
            if (isInternalLink(itemAction?.item)) {
              itemHref = itemAction.item.page
                ? getUrl(itemAction.item.page)
                : undefined;
            }

            return (
              <Flex
                key={item?.menu_action_sub_items_id?.id}
                align="center"
                gap={16}
                className="px-[18px] py-3"
              >
                {item?.menu_action_sub_items_id?.icon?.id && (
                  <Image
                    value={getDirectusImage(
                      item.menu_action_sub_items_id.icon.id,
                      {
                        format: "svg",
                        width: 24,
                      },
                    )}
                  />
                )}
                <Link
                  href={itemHref}
                  onClick={() => {
                    if (itemHref) {
                      closeBurgerMenu();
                      clickHeaderSub(itemHref);
                    }
                    if (nestedAction) {
                      onCallAction(nestedAction);
                    }
                  }}
                >
                  <Typography size="large">
                    {item?.menu_action_sub_items_id?.translations?.[0]?.name}
                  </Typography>
                </Link>
              </Flex>
            );
          })}
        </Accordion.Item>
      )}

      {variant === "underline" && (
        <Background
          bgValue="linear-gradient(90deg,#e2c1f9 0%,#febd8e 33%,#fbffe4 66%,#b6d0f7 100%);"
          className="mb-4 flex items-center justify-center rounded p-2"
        >
          <Link
            href={href}
            onClick={() => {
              if (href) {
                closeBurgerMenu();
                clickHeaderMain(href);
              }
              if (nestedAction) {
                onCallAction(nestedAction);
              }
            }}
          >
            <Typography
              size="large"
              weight="semibold"
              className="text-[#28282d]"
            >
              {translation?.name}
            </Typography>
          </Link>
        </Background>
      )}

      {variant === "emphasized" && (
        <Background
          bgValue="rgba(255, 255, 255, 0.1)"
          className="flex items-center justify-center transition-all duration-200 ease-linear py-2 md:py-4 rounded-lg mb-4"
        >
          <VipStarIcon
            width="24"
            height="24"
            viewBox="0 0 28 27"
            className="mx-1"
          />
          <Link
            href={href}
            onClick={() => {
              if (href) {
                closeBurgerMenu();
                clickHeaderMain(href);
              }
              if (nestedAction) {
                onCallAction(nestedAction);
              }
            }}
          >
            <Typography size="large" className="text-[#F1C144]">
              {translation?.name}
            </Typography>
          </Link>
          <VipStarIcon
            width="24"
            height="24"
            viewBox="0 0 28 27"
            className="mx-1"
          />
        </Background>
      )}

      {variant === "highlighted" && (
        <div className="pt-3 pb-4 text-center">
          <Background
            className="p-2 rounded w-full"
            bgValue="linear-gradient(271.26deg,#631cc6 1.4%,#bd75f5 46.85%,#631cc6 87.6%)"
          >
            <Link
              href={href}
              onClick={() => {
                if (href) {
                  closeBurgerMenu();
                  clickHeaderMain(href);
                }
                if (nestedAction) {
                  onCallAction(nestedAction);
                }
              }}
            >
              <Typography
                size="large"
                weight="semibold"
                className="text-[#fff]"
              >
                {translation?.name}
              </Typography>
            </Link>
          </Background>
        </div>
      )}
    </div>
  );
};
